import type {
  ApiErrorBody,
  CollectionResponse,
  CompletionResult,
  Device,
  DeviceCreationResult,
  OccurrenceFilters,
  OnceTemplateInput,
  OnceTemplatePatchInput,
  ParentMember,
  ReorderOccurrencesInput,
  ReorderedOccurrence,
  RuiGardenCommand,
  RuiGardenStatus,
  SessionResponse,
  TaskOccurrence,
  TaskTemplate,
  TemplateMutationResult,
  UndoResult,
  WeeklyStats,
} from "./types";

interface ApiClientOptions {
  fetcher?: typeof fetch;
  getCsrfToken?: () => string | null;
  createIdempotencyKey?: () => string;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH";
  body?: unknown;
  csrf?: boolean;
  idempotent?: boolean;
  idempotencyKey?: string;
}

const FALLBACK_ERROR_MESSAGE = "目前無法完成操作，請稍後再試。";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly details?: Record<string, unknown>;

  constructor(input: {
    status: number;
    code?: string;
    message?: string;
    requestId?: string;
    details?: Record<string, unknown>;
  }) {
    super(input.message || FALLBACK_ERROR_MESSAGE);
    this.name = "ApiError";
    this.status = input.status;
    this.code = input.code || "UNKNOWN_ERROR";
    this.requestId = input.requestId;
    this.details = input.details;
  }
}

export interface FamilyApi {
  getSession(): Promise<SessionResponse>;
  getMembers(): Promise<CollectionResponse<ParentMember>>;
  getTemplates(): Promise<CollectionResponse<TaskTemplate>>;
  getOccurrences(filters?: OccurrenceFilters): Promise<CollectionResponse<TaskOccurrence>>;
  createTemplate(input: OnceTemplateInput): Promise<TemplateMutationResult>;
  updateTemplate(templateId: string, input: OnceTemplatePatchInput): Promise<TemplateMutationResult>;
  archiveTemplate(templateId: string, expectedVersion: number, expectedOccurrenceVersion: number): Promise<TemplateMutationResult>;
  reorderOccurrences(input: ReorderOccurrencesInput): Promise<CollectionResponse<ReorderedOccurrence>>;
  completeOccurrence(occurrenceId: string, expectedVersion: number): Promise<CompletionResult>;
  undoCompletion(completionEventId: string, expectedVersion: number): Promise<UndoResult>;
  getWeeklyStats(weekStart: string): Promise<WeeklyStats>;
  getDevices(): Promise<CollectionResponse<Device>>;
  createDevice(name: string): Promise<DeviceCreationResult>;
  revokeDevice(deviceId: string, expectedVersion: number): Promise<{ device: Device }>;
  getRuiGardenStatus(): Promise<RuiGardenStatus>;
  setRuiGardenBrightness(value: number, idempotencyKey?: string): Promise<{ command: RuiGardenCommand }>;
  cancelRuiGardenCommand(commandId: string): Promise<{ command: RuiGardenCommand }>;
  logout(): Promise<void>;
  setCsrfToken?(token: string | null): void;
}

export class ApiClient implements FamilyApi {
  private readonly fetcher: typeof fetch;
  private readonly getCsrfToken: () => string | null;
  private readonly createIdempotencyKey: () => string;
  private csrfToken: string | null = null;

  constructor(options: ApiClientOptions = {}) {
    this.fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis);
    this.getCsrfToken = options.getCsrfToken ?? (() => this.csrfToken);
    this.createIdempotencyKey =
      options.createIdempotencyKey ?? (() => crypto.randomUUID());
  }

  setCsrfToken(token: string | null): void {
    this.csrfToken = token;
  }

  getSession(): Promise<SessionResponse> {
    return this.request<SessionResponse>("/api/v2/session");
  }

  getMembers(): Promise<CollectionResponse<ParentMember>> {
    return this.request("/api/v2/members");
  }

  getTemplates(): Promise<CollectionResponse<TaskTemplate>> {
    return this.request("/api/v2/task-templates");
  }

  createTemplate(input: OnceTemplateInput): Promise<TemplateMutationResult> {
    return this.request("/api/v2/task-templates", {
      method: "POST",
      body: input,
      csrf: true,
      idempotent: true,
    });
  }

  updateTemplate(
    templateId: string,
    input: OnceTemplatePatchInput,
  ): Promise<TemplateMutationResult> {
    return this.request(`/api/v2/task-templates/${encodeURIComponent(templateId)}`, {
      method: "PATCH",
      body: input,
      csrf: true,
      idempotent: true,
    });
  }

  getOccurrences(
    filters: OccurrenceFilters = {},
  ): Promise<CollectionResponse<TaskOccurrence>> {
    const query = new URLSearchParams();
    if (filters.from) query.set("from", filters.from);
    if (filters.to) query.set("to", filters.to);
    if (filters.assignee) query.set("assignee", filters.assignee);
    if (filters.status) query.set("status", filters.status);
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return this.request(`/api/v2/task-occurrences${suffix}`);
  }

  archiveTemplate(
    templateId: string,
    expectedVersion: number,
    expectedOccurrenceVersion: number,
  ): Promise<TemplateMutationResult> {
    return this.request(
      `/api/v2/task-templates/${encodeURIComponent(templateId)}/archive`,
      {
        method: "POST",
        body: {
          expectedVersion,
          expectedOccurrenceVersion,
        },
        csrf: true,
        idempotent: true,
      },
    );
  }


  reorderOccurrences(
    input: ReorderOccurrencesInput,
  ): Promise<CollectionResponse<ReorderedOccurrence>> {
    return this.request("/api/v2/task-occurrences/reorder", {
      method: "POST",
      body: input,
      csrf: true,
      idempotent: true,
    });
  }

  completeOccurrence(
    occurrenceId: string,
    expectedVersion: number,
  ): Promise<CompletionResult> {
    return this.request(
      `/api/v2/task-occurrences/${encodeURIComponent(occurrenceId)}/complete`,
      {
        method: "POST",
        body: { expectedVersion },
        csrf: true,
        idempotent: true,
      },
    );
  }

  undoCompletion(
    completionEventId: string,
    expectedVersion: number,
  ): Promise<UndoResult> {
    return this.request(
      `/api/v2/completions/${encodeURIComponent(completionEventId)}/undo`,
      {
        method: "POST",
        body: { expectedVersion },
        csrf: true,
        idempotent: true,
      },
    );
  }

  async logout(): Promise<void> {
    await this.request("/api/v2/auth/logout", {
      method: "POST",
      csrf: true,
    });
  }

  getWeeklyStats(weekStart: string): Promise<WeeklyStats> {
    return this.request(
      `/api/v2/stats/weekly?${new URLSearchParams({ weekStart }).toString()}`,
    );
  }

  getDevices(): Promise<CollectionResponse<Device>> {
    return this.request("/api/v2/devices");
  }

  createDevice(name: string): Promise<DeviceCreationResult> {
    return this.request("/api/v2/devices", {
      method: "POST",
      body: { name },
      csrf: true,
      idempotent: true,
    });
  }

  revokeDevice(
    deviceId: string,
    expectedVersion: number,
  ): Promise<{ device: Device }> {
    return this.request(`/api/v2/devices/${encodeURIComponent(deviceId)}/revoke`, {
      method: "POST",
      body: { expectedVersion },
      csrf: true,
      idempotent: true,
    });
  }

  getRuiGardenStatus(): Promise<RuiGardenStatus> {
    return this.request("/api/v2/ruigarden/status");
  }

  setRuiGardenBrightness(value: number, idempotencyKey?: string): Promise<{ command: RuiGardenCommand }> {
    return this.request("/api/v2/ruigarden/commands", {
      method: "POST",
      body: { kind: "set_brightness", value },
      csrf: true,
      idempotent: true,
      idempotencyKey,
    });
  }

  cancelRuiGardenCommand(commandId: string): Promise<{ command: RuiGardenCommand }> {
    return this.request(`/api/v2/ruigarden/commands/${encodeURIComponent(commandId)}/cancel`, {
      method: "POST",
      body: {},
      csrf: true,
    });
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const headers = new Headers({ Accept: "application/json" });
    if (options.body !== undefined) {
      headers.set("Content-Type", "application/json");
    }
    if (options.csrf) {
      const csrfToken = this.getCsrfToken();
      if (!csrfToken) {
        throw new ApiError({
          status: 401,
          code: "SESSION_REQUIRED",
          message: "登入狀態已失效，請重新登入。",
        });
      }
      headers.set("X-CSRF-Token", csrfToken);
    }
    if (options.idempotent) {
      headers.set("Idempotency-Key", options.idempotencyKey ?? this.createIdempotencyKey());
    }

    let response: Response;
    try {
      response = await this.fetcher(path, {
        method: options.method ?? "GET",
        credentials: "include",
        cache: "no-store",
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
      });
    } catch {
      throw new ApiError({
        status: 0,
        code: "NETWORK_ERROR",
        message: "無法連線。請確認網路後再試一次。",
      });
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const payload = await readJson(response);
    if (!response.ok) {
      const error = (payload as ApiErrorBody | null)?.error;
      throw new ApiError({
        status: response.status,
        code: error?.code,
        message: error?.message,
        requestId: error?.requestId,
        details: error?.details,
      });
    }
    return payload as T;
  }
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    if (!response.ok) {
      return null;
    }
    throw new ApiError({
      status: response.status,
      code: "INVALID_RESPONSE",
      message: FALLBACK_ERROR_MESSAGE,
    });
  }
}
